/**
 * AliasManagerDrawer (ORCH-115)
 *
 * Right-anchored MUI Drawer for managing alternate names on a master data
 * value. The drawer is the canonical full-edit surface for aliases —
 * `ValueFormModal` only shows a read-only summary in edit mode and a chip
 * input in create mode.
 *
 * Sections:
 *   - Header: value name + scope chip + close button.
 *   - Global aliases: read-only for non-admins; editable for global_*_admin
 *     in GLOBAL context (not yet implemented — admin manages those in the
 *     GLOBAL view).
 *   - Your aliases: inline edit / delete / add affordances for the active
 *     tenant's own aliases.
 *
 * Permission rendering mirrors the API permission table; if a backend 403
 * would result, the affordance is suppressed in the UI rather than rendered
 * and then blocked.
 *
 * Author: Development Team
 * Created: 2026-04-30 (ORCH-115)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  Button,
  Alert,
  Stack,
  Tooltip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { mdmService } from '../../services/mdmService';
import {
  MasterDataValue,
  MasterDataValueAlias,
  AliasCheckDuplicateResponse,
} from '../../types/mdm';
import { getScopeChipColor } from '../../constants/mdm';

export interface AliasManagerDrawerProps {
  open: boolean;
  onClose: () => void;
  value: MasterDataValue | null;
  /**
   * Active tenant of the calling user. Used to compute affordance visibility:
   * a tenant T sees their own aliases as editable; GLOBAL aliases are read-only
   * unless the user may manage GLOBAL aliases and is in GLOBAL context.
   */
  activeTenantId?: string;
  /**
   * Whether the caller may manage GLOBAL aliases: signed into GLOBAL and holding
   * `mdm.manage_aliases` (the server's `acts_globally`, ORCH-334). Combined with
   * `activeTenantId` to gate GLOBAL alias editing.
   */
  canManageGlobalAliases?: boolean;
  /**
   * Optional callback invoked after any alias mutation succeeds. Use this to
   * refresh the parent list / table cache.
   */
  onChanged?: () => void;
}

interface CollisionState {
  aliasId: string | null; // null when adding a new alias
  message: string;
  collisionKind: AliasCheckDuplicateResponse['collision_kind'];
}

const AliasManagerDrawer: React.FC<AliasManagerDrawerProps> = ({
  open,
  onClose,
  value,
  activeTenantId,
  canManageGlobalAliases = false,
  onChanged,
}) => {
  // Live alias list — always re-fetched on open so we see the inherit-and-extend
  // resolved set rather than the (capped) list-endpoint version.
  const [aliases, setAliases] = useState<MasterDataValueAlias[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Inline-edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>('');

  // Add state
  const [adding, setAdding] = useState<boolean>(false);
  const [addingText, setAddingText] = useState<string>('');

  // Collision shown inline next to whichever input triggered it
  const [collision, setCollision] = useState<CollisionState | null>(null);

  // A GLOBAL alias editor working in GLOBAL context can write to the global section
  // of a GLOBAL value.
  const isGlobalAliasEditor = canManageGlobalAliases && activeTenantId === 'GLOBAL';
  const isGlobalValue = value?.tenant_id === 'GLOBAL';
  const canEditGlobalAliases = isGlobalAliasEditor && isGlobalValue;

  // Load aliases when the drawer opens for a value.
  const loadAliases = useCallback(async () => {
    if (!value) return;
    setLoading(true);
    setError(null);
    try {
      const fresh = await mdmService.getValue(value.id);
      setAliases(fresh.aliases || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load aliases');
    } finally {
      setLoading(false);
    }
  }, [value]);

  useEffect(() => {
    if (open && value) {
      loadAliases();
      setEditingId(null);
      setEditingText('');
      setAdding(false);
      setAddingText('');
      setCollision(null);
    }
  }, [open, value, loadAliases]);

  if (!value) return null;

  const globalAliases = aliases.filter((a) => a.source === 'global');
  const localAliases = aliases.filter((a) => a.source === 'local');

  // The "Add alternate name" affordance renders inside whichever section is
  // editable. canEditGlobalAliases (admin in GLOBAL workspace viewing a GLOBAL
  // value) and the local section's editability (any tenant context) are
  // mutually exclusive, so a single shared `adding` state is sufficient.
  const renderAddAffordance = () =>
    adding ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1,
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 1,
        }}
      >
        <TextField
          autoFocus
          size="small"
          value={addingText}
          placeholder="New alternate name"
          onChange={(e) => {
            setAddingText(e.target.value);
            setCollision(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmitAdd();
            if (e.key === 'Escape') handleCancelAdd();
          }}
          error={collision?.aliasId === null}
          helperText={collision?.aliasId === null ? collision.message : undefined}
          sx={{ flexGrow: 1 }}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={handleSubmitAdd}
          aria-label="Save new alias"
        >
          <SaveIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleCancelAdd} aria-label="Cancel add">
          <CancelIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : (
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleStartAdd}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add alternate name
      </Button>
    );

  const handleStartEdit = (alias: MasterDataValueAlias) => {
    setEditingId(alias.id);
    setEditingText(alias.alias);
    setCollision(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText('');
    setCollision(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const trimmed = editingText.trim();
    if (!trimmed) {
      setCollision({
        aliasId: editingId,
        message: 'Alias cannot be empty.',
        collisionKind: null,
      });
      return;
    }
    setError(null);
    setCollision(null);
    try {
      const updated = await mdmService.updateAlias(value.id, editingId, trimmed);
      setAliases((prev) => prev.map((a) => (a.id === editingId ? { ...a, ...updated } : a)));
      setEditingId(null);
      setEditingText('');
      onChanged?.();
    } catch (e: any) {
      if (e.isConflict && e.conflict) {
        setCollision({
          aliasId: editingId,
          message: collisionMessage(e.conflict),
          collisionKind: e.conflict.collision_kind || null,
        });
      } else {
        setError(e.message || 'Failed to update alias');
      }
    }
  };

  const handleDelete = async (alias: MasterDataValueAlias) => {
    setError(null);
    try {
      await mdmService.deleteAlias(value.id, alias.id);
      setAliases((prev) => prev.filter((a) => a.id !== alias.id));
      onChanged?.();
    } catch (e: any) {
      setError(e.message || 'Failed to delete alias');
    }
  };

  const handleStartAdd = () => {
    setAdding(true);
    setAddingText('');
    setCollision(null);
  };

  const handleCancelAdd = () => {
    setAdding(false);
    setAddingText('');
    setCollision(null);
  };

  const handleSubmitAdd = async () => {
    const trimmed = addingText.trim();
    if (!trimmed) {
      setCollision({
        aliasId: null,
        message: 'Alias cannot be empty.',
        collisionKind: null,
      });
      return;
    }
    setError(null);
    setCollision(null);
    try {
      const created = await mdmService.addAlias(value.id, trimmed);
      // Idempotent inherit: backend may return an existing GLOBAL row. Only
      // append if it's not already in the list.
      setAliases((prev) => (prev.some((a) => a.id === created.id) ? prev : [...prev, created]));
      setAdding(false);
      setAddingText('');
      onChanged?.();
    } catch (e: any) {
      if (e.isConflict && e.conflict) {
        setCollision({
          aliasId: null,
          message: collisionMessage(e.conflict),
          collisionKind: e.conflict.collision_kind || null,
        });
      } else {
        setError(e.message || 'Failed to add alias');
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          // SkeletonPage's TopBanner sits at z-index `theme.zIndex.drawer + 1`
          // and would otherwise occlude the drawer header. Offset the paper so
          // the header (value name, scope chip, close button) stays visible.
          top: 64,
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {value.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={value.scope_info?.scope || 'GLOBAL'}
                size="small"
                color={getScopeChipColor(value.scope_info?.scope || 'GLOBAL')}
              />
              <Typography variant="caption" color="text.secondary">
                Manage alternate names
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} aria-label="Close drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {/* Global aliases section. Hidden for LOCAL values (no GLOBAL aliases possible). */}
            {isGlobalValue && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Global aliases
                </Typography>
                {globalAliases.length === 0 && !(canEditGlobalAliases && adding) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {canEditGlobalAliases ? 'No global aliases yet.' : 'None.'}
                  </Typography>
                )}
                <Stack spacing={1}>
                  {globalAliases.map((a) =>
                    canEditGlobalAliases && editingId === a.id ? (
                      <Box
                        key={a.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'primary.main',
                          borderRadius: 1,
                        }}
                      >
                        <TextField
                          autoFocus
                          size="small"
                          value={editingText}
                          onChange={(e) => {
                            setEditingText(e.target.value);
                            setCollision(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          error={collision?.aliasId === a.id}
                          helperText={collision?.aliasId === a.id ? collision.message : undefined}
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleSaveEdit}
                          aria-label="Save alias"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          aria-label="Cancel edit"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        key={a.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Tooltip
                          title={
                            canEditGlobalAliases
                              ? 'GLOBAL alias'
                              : 'Inherited from GLOBAL — manage from the GLOBAL view'
                          }
                        >
                          <Chip label={a.alias} size="small" variant="outlined" />
                        </Tooltip>
                        {canEditGlobalAliases && (
                          <>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(a)}
                              aria-label={`Edit alias ${a.alias}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(a)}
                              aria-label={`Delete alias ${a.alias}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    )
                  )}
                  {canEditGlobalAliases && renderAddAffordance()}
                </Stack>
              </Box>
            )}

            {/* Tenant (local) aliases section. Hidden when in GLOBAL context. */}
            {activeTenantId !== 'GLOBAL' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Your aliases
                </Typography>
                {localAliases.length === 0 && !adding && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    You haven't added any alternate names yet.
                  </Typography>
                )}
                <Stack spacing={1}>
                  {localAliases.map((a) =>
                    editingId === a.id ? (
                      <Box
                        key={a.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'primary.main',
                          borderRadius: 1,
                        }}
                      >
                        <TextField
                          autoFocus
                          size="small"
                          value={editingText}
                          onChange={(e) => {
                            setEditingText(e.target.value);
                            setCollision(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          error={collision?.aliasId === a.id}
                          helperText={collision?.aliasId === a.id ? collision.message : undefined}
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleSaveEdit}
                          aria-label="Save alias"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          aria-label="Cancel edit"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        key={a.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Chip label={a.alias} size="small" color="primary" />
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(a)}
                          aria-label={`Edit alias ${a.alias}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(a)}
                          aria-label={`Delete alias ${a.alias}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                  )}

                  {renderAddAffordance()}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

/** Shape the API's `AliasCheckDuplicateResponse` into a user-facing message. */
function collisionMessage(conflict: any): string {
  const colliding = conflict?.colliding_value_name;
  switch (conflict?.collision_kind) {
    case 'name':
      return colliding
        ? `Collides with the value name '${colliding}'.`
        : 'Collides with another value name.';
    case 'alias':
      return colliding
        ? `Collides with an existing alias on '${colliding}'.`
        : 'Collides with an existing alias.';
    case 'underlying_canonical':
      return 'Matches the original name of this value before your override. Adding it would conflict if you ever revert.';
    default:
      return conflict?.message || 'This alternate name is not available.';
  }
}

export default AliasManagerDrawer;
